/**
 * AsyncTagSelect Component
 * 
 * COPIED FROM: @gp/components/AsyncTagSelect
 * 
 * WHY WE COPIED:
 * The original component has incorrect highlighting behavior that doesn't match user expectations:
 * - When typing "123", it highlights individual characters "1", "2", "3" separately anywhere they appear
 * - This causes "12333" to be highlighted as "1", "2", "333" separately, even though "123" isn't a continuous substring
 * - Users expect exact substring matching (e.g., "123" should only match continuous "123")
 * 
 * MODIFICATIONS:
 * 1. Added `strictSubstringMatch` prop (defaults to true)
 *    - When true: uses exact substring matching (isSeparate: false)
 *    - When false: uses character-by-character matching (original behavior)
 * 2. Updated `formatOptions` to pass `isSeparate: !strictSubstringMatch` to getTextWithHighlight
 * 3. Changed class name prefix from 'dbg-' to 'dbs-' to follow our system's naming convention
 * 
 * USAGE:
 * <AsyncTagSelect
 *   fetchOptions={fetchUsers}
 *   strictSubstringMatch={true} // Exact substring matching (default)
 * />
 */

import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { TagSelect, TagSelectProps } from 'tea-component';
import DBGStatusTip, { IDBGStatusTipProps } from '@gp/components/TeaComponents/StatusTip';
import { getTextWithHighlight } from '@/pages/intelligence/utils/commonUtils';

import './index.scss';

export interface IAsyncTagSelectProps extends TagSelectProps {
  // 获取options的service
  fetchOptions: (keyword: string) => Promise<IGPSelectOptionWithGroup[]>;
  statusTipProps?: IDBGStatusTipProps;
  // 是否启用严格子串匹配（而不是字符拆分匹配）
  strictSubstringMatch?: boolean;
}

const filterOptions = (_payload: string, _option: IGPSelectOptionWithGroup): boolean => true;

/**
 * Format options with highlighted text
 * Uses substring matching instead of character-by-character matching when strictSubstringMatch is true
 */
const formatOptions = (
  options?: IGPSelectOptionWithGroup[],
  keyword?: string,
  strictSubstringMatch?: boolean,
): IGPSelectOptionWithGroup[] => {
  if (!Array.isArray(options) || !options.length) return [];
  return options.map((option) => {
    const { text, ...restOption } = option || {};
    return {
      ...restOption,
      text: getTextWithHighlight(text as string, keyword || '', {
        isSeparate: !strictSubstringMatch, // false = exact substring matching, true = character-by-character
        isIgnoreCase: true,
      }),
    };
  });
};

const AsyncTagSelect: React.FC<IAsyncTagSelectProps> = (props) => {
  const {
    className,
    overlayClassName,
    optionsOnly = true,
    filter = filterOptions,
    fetchOptions,
    value: valueProps,
    onChange,
    statusTipProps,
    strictSubstringMatch = true, // Default to strict substring matching for better UX
    ...restProps
  } = props;

  const { onRetry, className: statusTipClassName, ...restStatusTipProps } = statusTipProps || {};

  const timer = useRef<NodeJS.Timeout | null>(null);

  // 最后输入的关键字（确保多次请求后，请求返回的数据能跟对应的关键字匹配时，才刷新数据）
  const keyword = useRef('');

  const [status, setStatus] = useState<IDBGStatusTipProps['status'] | null>(null);

  const [options, setOptions] = useState<IGPSelectOptionWithGroup[]>([]);

  const [value, setValue] = useState<string[]>(valueProps || []);

  const handleSearch = async (payload?: string) => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }

    const payloadTmp = payload?.trim() || '';
    keyword.current = payloadTmp;
    setStatus('loading');
    setOptions([]);

    const search = async () => {
      try {
        const optionsTmp = await fetchOptions(payloadTmp);
        if (!Array.isArray(optionsTmp) || !optionsTmp.length) {
          setStatus('empty');
          return;
        }
        if (keyword.current === payloadTmp) {
          setStatus(null);
          setOptions(formatOptions(optionsTmp, payloadTmp, strictSubstringMatch));
        }
      } catch (exception) {
        console.error(exception);
        setStatus('error');
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    timer.current = setTimeout(search, 300);
  };

  useEffect(() => {
    setValue(valueProps || []);
  }, [valueProps]);

  return (
    <TagSelect
      {...restProps}
      className={classnames('dbs-async-tag-select', className)}
      overlayClassName={classnames('dbs-async-tag-select-overlay', overlayClassName)}
      optionsOnly={optionsOnly}
      options={options}
      filter={filter}
      value={value}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSearch={handleSearch}
      onChange={(payload, ctx) => {
        setOptions([]);
        setValue(payload);
        onChange?.(payload, ctx);
      }}
      tips={
        status && (
          <DBGStatusTip
            {...restStatusTipProps}
            className={classnames('dbs-async-tag-select-status-tip', statusTipClassName)}
            status={status}
            onRetry={() => {
              handleSearch(keyword.current);
              onRetry?.();
            }}
          />
        )
      }
    />
  );
};

export default AsyncTagSelect;
